// Admin capability model — mirrors waive_core.auth.dependencies (ISO 27001 A.5.3).
//
// The API is the security boundary: admin-api enforces these roles and will 403 regardless of what the
// UI shows. This module exists so the UI does not offer actions the user cannot perform — a button that
// always 403s is worse than no button.
//
// Keep in step with waive-core. If the two disagree, the API wins and the user sees a 403.

export const ROLE_ADMIN = 'waive-admin';                  // superset — satisfies every check
export const ROLE_READER = 'waive-reader';                // all reads EXCEPT member data
export const ROLE_MEMBER_READER = 'waive-member-reader';  // member identity + advice history
export const ROLE_ONBOARDING = 'waive-onboarding';        // tenant lifecycle, IdP, subdomain
export const ROLE_CONFIG = 'waive-config';                // advice config + platform catalogue
export const ROLE_TRAINING = 'waive-training';            // train, gate, version — NOT release
export const ROLE_RELEASE = 'waive-release';              // release a model to tenants

export const ADMIN_ROLES = [
    ROLE_ADMIN, ROLE_READER, ROLE_MEMBER_READER,
    ROLE_ONBOARDING, ROLE_CONFIG, ROLE_TRAINING, ROLE_RELEASE,
];

/** Does this user hold any admin role at all? Governs whether they may use the admin app. */
export function isAdminUser(roles) {
    return Array.isArray(roles) && roles.some(r => ADMIN_ROLES.includes(r));
}

/** Holds `role`, or the admin superset. */
export function hasRole(roles, role) {
    if (!Array.isArray(roles)) return false;
    return roles.includes(ROLE_ADMIN) || roles.includes(role);
}

// Capabilities, named for what the user is trying to DO rather than which role they hold — so a
// component reads `can.editAdviceConfig(roles)` and does not need to know the role model.
export const can = {
    // Reads of non-member data are open to any admin role: you cannot onboard a tenant without
    // reading tenants, or release a model without reading training runs.
    read: roles => isAdminUser(roles),
    readMemberData: roles => hasRole(roles, ROLE_MEMBER_READER),
    onboardTenant: roles => hasRole(roles, ROLE_ONBOARDING),
    editAdviceConfig: roles => hasRole(roles, ROLE_CONFIG),
    editCatalogue: roles => hasRole(roles, ROLE_CONFIG),
    trainModel: roles => hasRole(roles, ROLE_TRAINING),
    releaseModel: roles => hasRole(roles, ROLE_RELEASE),
};
